import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnInit,
    ViewEncapsulation,
    input,
    output,
    signal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import {
    DataIngestionService,
    IngestionPoint,
    ReportingFieldInfo,
    SearchRequest,
    ReportRequest,
    AggregationSpec,
    SearchResponse,
    ReportResponse,
    SavedQuery,
    IngestionSubmission,
    AnswerType,
    IngestionFieldAnswer,
} from "./data.ingestion.service";
import { PredicateNode } from "./ingestion.predicate.builder.component";
import { ContactService } from "../../services/contact.service";
import { Table } from "../s25-table/Table";
import { S25TableModule } from "../s25-table/s25.table.module";
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";

@TypeManagerDecorator("s25-ng-ingestion-search-report")
@Component({
    selector: "s25-ng-ingestion-search-report",
    template: `
        <div class="ingestion-search-report">
            <div class="mode-selection">
                <h3>Ingestion Search & Reporting</h3>
                <div class="mode-toggle">
                    <div class="toggle-container">
                        <button class="toggle-option" [class.active]="mode() === 'search'" (click)="setMode('search')">
                            Search
                        </button>
                        <button class="toggle-option" [class.active]="mode() === 'report'" (click)="setMode('report')">
                            Report
                        </button>
                    </div>
                </div>

                <div class="saved-queries">
                    <label>Saved Queries:</label>
                    <select class="c-input" (change)="loadSavedQuery($event)" [value]="currentSavedQuery()?.id || ''">
                        <option value="">Select a saved query...</option>
                        @for (query of savedQueries(); track query.id) {
                            <option [value]="query.id">{{ query.name }} ({{ query.type }})</option>
                        }
                    </select>
                    @if (currentSavedQuery()) {
                        <button class="aw-button aw-button--danger-outline" (click)="deleteSavedQuery()">Delete</button>
                    }
                </div>

                <div class="save-name-input">
                    <label>Query Name:</label>
                    <input
                        class="c-input"
                        type="text"
                        [(ngModel)]="saveName"
                        placeholder="Enter a name for this query..."
                        [disabled]="!canSave()"
                    />
                </div>
            </div>

            <div class="form-selection">
                <h4>Select Forms</h4>
                <s25-ng-ingestion-search-form-selector
                    [selectedForms]="selectedForms()"
                    [searchAllForms]="searchAllForms()"
                    (formsChanged)="onFormsChanged($event)"
                    (searchAllChanged)="onSearchAllChanged($event)"
                >
                </s25-ng-ingestion-search-form-selector>
            </div>

            @if (availableFields().length > 0) {
                <div class="field-selection">
                    <h4>{{ mode() === "search" ? "Select Fields to Display" : "Select Fields to Group By" }}</h4>
                    <s25-ng-ingestion-field-selector
                        [availableFields]="availableFields()"
                        [selectedFields]="getSelectedFieldsForSelector()"
                        [mode]="mode()"
                        (fieldsChanged)="onFieldSelectionChanged($event)"
                    >
                    </s25-ng-ingestion-field-selector>
                </div>
            }

            @if (mode() === "report") {
                <div class="aggregation-selection">
                    <h4>Select Aggregations</h4>
                    <s25-ng-aggregation-selector
                        [availableFields]="availableFields()"
                        [selectedAggregations]="selectedAggregations()"
                        (aggregationsChanged)="onAggregationsChanged($event)"
                    >
                    </s25-ng-aggregation-selector>
                </div>
            }

            <div class="predicate-builder">
                <h4>Build Search Criteria</h4>
                <s25-ng-predicate-builder
                    [availableFields]="availableFields()"
                    [predicate]="predicate()"
                    (predicateChanged)="onPredicateChanged($event)"
                >
                </s25-ng-predicate-builder>
            </div>

            <div class="actions">
                <button class="aw-button aw-button--primary" (click)="execute()" [disabled]="!canExecute()">
                    {{ mode() === "search" ? "Search" : "Generate Report" }}
                </button>
                <button
                    class="aw-button aw-button--outline"
                    (click)="save()"
                    [disabled]="!canSave() || !saveName()?.trim()"
                >
                    Save
                </button>
                <button
                    class="aw-button aw-button--outline"
                    (click)="saveAs()"
                    [disabled]="!canSave() || !saveName()?.trim()"
                >
                    Save As
                </button>
            </div>

            @if (init()) {
                @if (mode() === "search" && searchTableConfig()) {
                    <div class="results">
                        <h4>Results</h4>
                        <s25-ng-table
                            [dataSource]="searchTableConfig()!"
                            [columnSortable]="false"
                            [unlimitedWidth]="true"
                            [hasTotalRowCount]="true"
                            [hasRefresh]="true"
                        >
                        </s25-ng-table>
                    </div>
                }
                @if (mode() === "report" && reportTableConfig()) {
                    <div class="results">
                        <h4>Results</h4>
                        <s25-ng-table
                            [dataSource]="reportTableConfig()!"
                            [columnSortable]="true"
                            [unlimitedWidth]="true"
                            [hasTotalRowCount]="true"
                            [hasRefresh]="true"
                        >
                        </s25-ng-table>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .ingestion-search-report {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .mode-selection {
            margin-bottom: 30px;
        }

        .mode-toggle {
            margin-top: 15px;
        }

        .toggle-container {
            display: inline-flex;
            background: #f5f5f5;
            border-radius: 8px;
            padding: 4px;
            border: 1px solid #ddd;
        }

        .toggle-option {
            padding: 8px 16px;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            color: #666;
            transition: all 0.2s ease;
            min-width: 80px;
        }

        .toggle-option.active {
            background: white;
            color: #007bff;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .toggle-option:hover:not(.active) {
            color: #333;
        }

        .saved-queries {
            margin-top: 15px;
            padding: 10px;
            background-color: #f8f9fa;
            border-radius: 4px;

            label {
                display: block;
                margin-bottom: 5px;
                font-weight: bold;
            }

            select {
                width: 100%;
                max-width: 300px;
                margin-right: 10px;
            }

            button {
                margin-left: 10px;
            }
        }

        .save-name-input {
            margin-top: 15px;
            padding: 10px;
            background-color: #f8f9fa;
            border-radius: 4px;

            label {
                display: block;
                margin-bottom: 5px;
                font-weight: bold;
            }

            input {
                width: 100%;
                max-width: 300px;
                padding: 8px 12px;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 14px;
            }

            input:disabled {
                background-color: #f5f5f5;
                color: #999;
            }
        }

        .form-selection,
        .field-selection,
        .aggregation-selection,
        .predicate-builder {
            margin-bottom: 30px;
            padding: 20px;
            border: 1px solid #ddd;
            border-radius: 4px;
        }

        .actions {
            margin-bottom: 30px;
            display: flex;
            gap: 10px;
        }

        .btn {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }

        .btn-primary {
            background: #007bff;
            color: white;
        }

        .btn-secondary {
            background: #6c757d;
            color: white;
        }

        .btn-secondary:hover:not(:disabled) {
            background: #5a6268;
        }

        .btn-secondary:disabled {
            background: #6c757d;
            opacity: 0.6;
            cursor: not-allowed;
        }

        .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .results {
            margin-top: 30px;
        }

        .no-results {
            text-align: center;
            padding: 40px 20px;
            color: #666;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
        }

        h3,
        h4 {
            margin-top: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionSearchReportComponent implements OnInit {
    initialMode = input<"search" | "report">("search");
    resultsChanged = output<any>();

    mode = signal<"search" | "report">("search");
    selectedForms = signal<IngestionPoint[]>([]);
    searchAllForms = signal<boolean>(false);
    availableFields = signal<ReportingFieldInfo[]>([]);
    selectedFieldInfos = signal<ReportingFieldInfo[]>([]);
    selectedAggregations = signal<AggregationSpec[]>([]);
    predicate = signal<PredicateNode | null>(null);
    results = signal<SearchResponse | ReportResponse | null>(null);
    searchTableConfig = signal<Table.DataSource | null>(null);
    reportTableConfig = signal<Table.DataSource | null>(null);
    savedQueries = signal<SavedQuery[]>([]);
    currentSavedQuery = signal<SavedQuery | null>(null);
    loading = signal<boolean>(false);
    currentPageKey = signal<string | null>(null);
    saveName = signal<string>("");
    init = signal(false);
    dateTimeFormat = signal<string>("");
    specialFieldNames = signal<Map<string, string>>(new Map());

    constructor(
        private elem: ElementRef,
        private cd: ChangeDetectorRef,
        private z: NgZone,
    ) {}

    async ngOnInit() {
        try {
            const format = await UserprefService.getS25DateTimeformat();
            this.dateTimeFormat.set(format);
        } catch (error) {
            console.error("Error loading date/time format:", error);
            // Set fallback format
            this.dateTimeFormat.set("MMM dd, yyyy h:mm a");
        } finally {
            this.init.set(true);
        }

        this.mode.set(this.initialMode());
        this.loadIngestionPoints();
        this.loadSavedQueries();
    }

    async loadIngestionPoints() {
        try {
            // Don't pre-select any forms - let user select manually
            this.selectedForms.set([]);
            // Load available fields after setting forms
            this.loadAvailableFields();
        } catch (error) {
            console.error("Error loading ingestion points:", error);
        }
    }

    async loadSavedQueries() {
        try {
            const queries = await DataIngestionService.getSavedQueries();
            this.savedQueries.set(queries);
        } catch (error) {
            console.error("Error loading saved queries:", error);
        }
    }

    setMode(mode: "search" | "report") {
        this.mode.set(mode);
        this.selectedFieldInfos.set([]);
        this.selectedAggregations.set([]);
        this.selectedForms.set([]);
        this.searchAllForms.set(false);
        this.results.set(null);
        this.availableFields.set([]);
        this.cd.detectChanges();
    }

    onFormsChanged(forms: IngestionPoint[]) {
        this.selectedForms.set(forms);
        this.resetPageKey(); // Reset page key when search criteria change
        this.loadAvailableFields();
        this.cd.detectChanges();
    }

    onSearchAllChanged(searchAll: boolean) {
        this.searchAllForms.set(searchAll);
        this.resetPageKey(); // Reset page key when search criteria change
        this.loadAvailableFields();
        this.cd.detectChanges();
    }

    onFieldSelectionChanged(fields: ReportingFieldInfo[]) {
        this.selectedFieldInfos.set(fields);
        this.resetPageKey(); // Reset page key when search criteria change
    }

    getSelectedFieldsForSelector(): ReportingFieldInfo[] {
        return this.selectedFieldInfos();
    }

    onAggregationsChanged(aggregations: AggregationSpec[]) {
        this.selectedAggregations.set(aggregations);
    }

    onPredicateChanged(predicate: any) {
        this.predicate.set(predicate);
        this.resetPageKey(); // Reset page key when search criteria change
    }

    resetPageKey() {
        this.currentPageKey.set(null);
    }

    async loadAvailableFields() {
        if (this.selectedForms().length === 0 && !this.searchAllForms()) {
            this.availableFields.set([]);
            this.cd.detectChanges();
            return;
        }

        this.loading.set(true);
        try {
            const formIds = this.selectedForms().map((f) => f.ingestionPointId!);
            const fields = await DataIngestionService.getDistinctReportingFields(formIds, this.searchAllForms());
            this.availableFields.set(fields);
        } catch (error) {
            console.error("Error loading available fields:", error);
        } finally {
            this.loading.set(false);
            this.cd.detectChanges();
        }
    }

    canExecute(): boolean {
        if (this.mode() === "search") {
            return this.selectedFieldInfos().length > 0;
        } else {
            // For reports, need at least one complete aggregation
            return (
                this.selectedAggregations().length > 0 &&
                this.selectedAggregations().every((agg) => this.isAggregationComplete(agg))
            );
        }
    }

    isAggregationComplete(aggregation: AggregationSpec): boolean {
        switch (aggregation.type) {
            case "COUNT":
                return aggregation.field === "*";
            case "COUNT_DISTINCT":
                return (
                    aggregation.field === "cont_id" ||
                    this.availableFields().some((f) => f.reportingFieldCode === aggregation.field)
                );
            case "SUM":
            case "AVG":
                return this.availableFields().some(
                    (f) =>
                        f.reportingFieldCode === aggregation.field &&
                        (f.answerType === AnswerType.INTEGER || f.answerType === AnswerType.FLOAT),
                );
            default:
                return false;
        }
    }

    canSave(): boolean {
        return this.canExecute();
    }

    canSaveAs(): boolean {
        return this.canExecute() && this.currentSavedQuery() !== null;
    }

    async loadSavedQuery(event: Event) {
        // TODO: Implement this
        console.log("TODO: Implement loadSavedQuery", event);
        const target = event.target as HTMLSelectElement;
        const queryId = parseInt(target.value);

        if (!queryId) {
            this.currentSavedQuery.set(null);
            return;
        }

        try {
            const query = this.savedQueries().find((q) => q.id === queryId);
            if (query) {
                this.currentSavedQuery.set(query);
                // Load the saved query data and populate the form
                await this.loadSavedQueryData(query);
            }
        } catch (error) {
            console.error("Error loading saved query:", error);
        }
    }

    async loadSavedQueryData(query: SavedQuery) {
        // TOOD: Implement this
        console.log("TODO: Implement loadSavedQueryData", query);

        // This would load the actual query data from the backend
        // For now, just set the mode based on query type
        if (query.type === "SEARCH") {
            this.setMode("search");
        } else {
            this.setMode("report");
        }
    }

    async deleteSavedQuery() {
        if (!this.currentSavedQuery()) return;

        alert("Delete not implemented");

        try {
            await DataIngestionService.deleteSavedQuery(this.currentSavedQuery()!.id);
            this.currentSavedQuery.set(null);
            await this.loadSavedQueries(); // Refresh the list
        } catch (error) {
            console.error("Error deleting saved query:", error);
        }
    }

    async save() {
        if (!this.canSave() || !this.saveName()?.trim()) return;

        alert("Save not implemented");

        if (this.currentSavedQuery()) {
            // Update existing saved query
            await this.updateSavedQuery();
        } else {
            // Create new saved query
            await this.createSavedQuery(this.saveName()!.trim());
        }
    }

    async saveAs() {
        if (!this.canSave() || !this.saveName()?.trim()) return;

        alert("Save as not implemented");

        await this.createSavedQuery(this.saveName()!.trim());
    }

    async createSavedQuery(name: string) {
        try {
            if (this.mode() === "search") {
                const request: SearchRequest = {
                    ingestionPointIds: this.selectedForms().map((f) => f.ingestionPointId!),
                    searchAllForms: this.searchAllForms(),
                    projectionFields: this.selectedFieldInfos().map((f) => f.reportingFieldCode),
                    predicate: this.predicate(),
                    pageNum: 1,
                    pageSize: 50,
                };
                await DataIngestionService.saveSearch(name, request);
            } else {
                const request: ReportRequest = {
                    ingestionPointIds: this.selectedForms().map((f) => f.ingestionPointId!),
                    searchAllForms: this.searchAllForms(),
                    groupByFields: this.selectedFieldInfos(),
                    aggregations: this.selectedAggregations(),
                    predicate: this.predicate(),
                    includeForm: this.selectedFieldInfos().some((f) => f.reportingFieldCode === "form"),
                };
                await DataIngestionService.saveReport(name, request);
            }
            await this.loadSavedQueries(); // Refresh the list
        } catch (error) {
            console.error("Error saving query:", error);
        }
    }

    async updateSavedQuery() {
        // Implementation for updating existing saved query
        // This would call the backend to update the existing query
        console.log("Update saved query:", this.currentSavedQuery());
    }

    async execute() {
        this.loading.set(true);
        try {
            // Reset results and clear table configs to force rebuild
            this.results.set(null);
            if (this.mode() === "search") {
                this.searchTableConfig.set(null);
            } else {
                this.reportTableConfig.set(null);
            }

            setTimeout(async () => {
                // Build table config - let the table's data source handle the actual search/report
                await this.buildTableConfig();
                this.resultsChanged.emit(this.results());
            }, 0);
        } catch (error) {
            console.error("Error executing:", error);
        } finally {
            this.loading.set(false);
        }
    }

    async executeSearch(pageNum: number = 1, pageSize: number = 50): Promise<SearchResponse> {
        const request: SearchRequest = {
            ingestionPointIds: this.selectedForms().map((f) => f.ingestionPointId!),
            searchAllForms: this.searchAllForms(),
            projectionFields: this.selectedFieldInfos().map((f) => f.reportingFieldCode),
            predicate: this.predicate(),
            pageNum: pageNum,
            pageSize: pageSize,
            pageKey: pageNum === 1 ? undefined : this.currentPageKey() || undefined,
        };

        const response = await DataIngestionService.executeSearch(request);

        // Update the current page key
        if (response.pageCacheKey) {
            this.currentPageKey.set(response.pageCacheKey);
        }

        return response;
    }

    async executeReport(): Promise<ReportResponse> {
        const request: ReportRequest = {
            ingestionPointIds: this.selectedForms().map((f) => f.ingestionPointId!),
            searchAllForms: this.searchAllForms(),
            groupByFields: this.selectedFieldInfos(),
            aggregations: this.selectedAggregations(),
            predicate: this.predicate(),
            includeForm: this.selectedFieldInfos().some((f) => f.reportingFieldCode === "form"),
        };

        const response = await DataIngestionService.executeReport(request);
        return response;
    }

    async buildTableConfig() {
        if (this.mode() === "search") {
            await this.buildSearchTableConfig();
        } else {
            await this.buildReportTableConfig();
        }
    }

    // Note: Tables are kept separate and shown/hidden based on mode to avoid re-running WS calls

    async buildSearchTableConfig() {
        const columns: Table.Column[] = [
            { header: "Submission ID", id: "submissionId" },
            { header: "Form", id: "form" },
            { header: "Contact Name", id: "contactName" },
            { header: "Submission Date", id: "submissionDate", type: "date" },
        ];

        // Add selected field columns
        const selectedFields = this.selectedFieldInfos();
        selectedFields.forEach((field) => {
            columns.push({
                header: field.reportingFieldCode,
                id: field.reportingFieldCode,
            });
        });

        this.searchTableConfig.set({
            type: "paginated",
            columns,
            dataSource: async (query: Table.PaginatedQuery) => {
                // Don't run WS if no fields are selected (e.g., when switching modes)
                if (this.selectedFieldInfos().length === 0) {
                    return { rows: [], totalRows: 0 };
                }

                // Use the pure executeSearch function
                const response = await this.executeSearch(query.page + 1, query.pageSize || 50);

                // Load contact names and special field names for this page
                const pageContactNames = await this.loadContactNames(response.data as IngestionSubmission[]);
                const pageSpecialFieldNames = await this.loadSpecialFieldNames(response.data as IngestionSubmission[]);

                // Build rows for this page using the selected fields
                const pageRows = await this.buildRowsForPage(
                    response.data as IngestionSubmission[],
                    pageContactNames,
                    pageSpecialFieldNames,
                    selectedFields.map((f) => f.reportingFieldCode),
                );

                return { rows: pageRows, totalRows: response.total };
            },
            startPage: 0,
            defaultPageSize: 50,
            pageSizes: [25, 50, 100],
        });
    }

    async buildRowsForPage(
        submissions: IngestionSubmission[],
        contactNames: Map<number, string>,
        specialFieldNames: Map<string, string>,
        fieldColumns: string[],
    ): Promise<Table.Row[]> {
        return submissions.map((submission) => {
            const cells: Record<string, Table.Cell> = {
                submissionId: { text: submission.submissionId },
                form: { text: submission.ingestionPoint?.ingestionPointName || "" },
                contactName: { text: contactNames.get(submission.contId) || `Contact ${submission.contId}` },
                submissionDate: { text: this.formatDate(submission.submissionDate) },
            };

            // Add field values
            fieldColumns.forEach((field) => {
                cells[field] = { text: this.getFieldValue(submission, field, specialFieldNames) };
            });

            return {
                id: submission.submissionId,
                name: submission.submissionId.toString(),
                cells,
            };
        });
    }

    async buildReportTableConfig() {
        // Build columns from groupBy fields and aggregations (no need to execute report first)
        const columns: Table.Column[] = [];

        // Add group by field columns
        const groupByFields = this.selectedFieldInfos();
        if (groupByFields) {
            groupByFields.forEach((fieldInfo) => {
                if (fieldInfo.reportingFieldCode === "form") {
                    columns.push({ header: "Form", id: "form_name", sortable: true });
                } else if (fieldInfo.reportingFieldCode === "cont_id") {
                    columns.push({ header: "Contact ID", id: "cont_id", sortable: true });
                } else {
                    // Regular field - use the field name as both header and id
                    columns.push({
                        header: fieldInfo.reportingFieldCode,
                        id: fieldInfo.reportingFieldCode,
                        sortable: true,
                    });
                }
            });
        }

        // Add aggregation columns
        const aggregations = this.selectedAggregations();
        if (aggregations) {
            aggregations.forEach((agg) => {
                let header: string;
                let id: string;

                switch (agg.type) {
                    case "COUNT":
                        header = "Count";
                        id = "count_all";
                        break;
                    case "COUNT_DISTINCT":
                        if (agg.field === "cont_id") {
                            header = "Distinct Contacts";
                            id = "distinct_cont_id";
                        } else {
                            header = `Distinct ${agg.field}`;
                            id = `distinct_${agg.field}`;
                        }
                        break;
                    case "SUM":
                        header = `Sum of ${agg.field}`;
                        id = `sum_${agg.field}`;
                        break;
                    case "AVG":
                        header = `Average ${agg.field}`;
                        id = `avg_${agg.field}`;
                        break;
                    default:
                        header = `${agg.type} ${agg.field}`;
                        id = `${(agg.type as string).toLowerCase()}_${agg.field}`;
                }

                columns.push({ header, id, sortable: true });
            });
        }

        // Set the table config with proper columns
        this.reportTableConfig.set({
            type: "unpaginated",
            columns,
            dataSource: async (query: Table.UnpaginatedQuery) => {
                // Don't run WS if no fields/aggregations are selected (e.g., when switching modes)
                if (
                    this.selectedFieldInfos().length === 0 ||
                    this.selectedAggregations().length === 0 ||
                    !this.selectedAggregations().every((agg) => this.isAggregationComplete(agg))
                ) {
                    return { rows: [] };
                }

                // Execute the report and get the data (always execute - no caching for reports)
                const response = await this.executeReport();
                const reportData = response.data || [];

                if (reportData.length === 0) {
                    return { rows: [] };
                }

                // Accumulate special field IDs from report data and load names
                const { eventTypeIds, organizationIds } = this.accumulateSpecialFieldIdsFromReport(
                    reportData,
                    this.selectedFieldInfos(),
                );
                const specialFieldNames = await this.loadNamesForSpecialFieldIds(eventTypeIds, organizationIds);

                // Build rows from the data
                const rows: Table.Row[] = reportData.map((row, index) => {
                    const cells: Record<string, Table.Cell> = {};

                    // Add cells for each column
                    columns.forEach((column) => {
                        cells[column.id] = {
                            text: this.getReportFieldValue(
                                row,
                                String(column.id),
                                this.selectedFieldInfos(),
                                specialFieldNames,
                            ),
                        };
                    });

                    return {
                        id: index,
                        name: index.toString(),
                        cells,
                    };
                });

                return { rows };
            },
        });
    }

    async loadContactNames(submissions: IngestionSubmission[]): Promise<Map<number, string>> {
        const contactIds = [...new Set(submissions.map((s) => s.contId).filter((id) => id))];

        if (contactIds.length === 0) {
            return new Map();
        }

        try {
            const contactNames = await ContactService.getContactsNameAndFormal(contactIds);
            return new Map(contactNames.map((c) => [Number(c.itemId), c.itemName]));
        } catch (error) {
            console.error("Error fetching contact names:", error);
            return new Map(contactIds.map((id) => [id, `Contact ${id}`]));
        }
    }

    /**
     * Accumulate special field IDs (EVENT_TYPE, ORGANIZATION) from submissions
     */
    accumulateSpecialFieldIds(submissions: IngestionSubmission[]): {
        eventTypeIds: Set<number>;
        organizationIds: Set<number>;
    } {
        const eventTypeIds = new Set<number>();
        const organizationIds = new Set<number>();

        submissions.forEach((submission) => {
            if (submission.answers) {
                submission.answers.forEach((answer) => {
                    const fieldType = answer.fieldDefinition?.fieldType;
                    const answerRaw = answer.answerRaw;

                    if (fieldType === "EVENT_TYPE" && answerRaw && !isNaN(Number(answerRaw))) {
                        eventTypeIds.add(Number(answerRaw));
                    } else if (fieldType === "ORGANIZATION" && answerRaw && !isNaN(Number(answerRaw))) {
                        organizationIds.add(Number(answerRaw));
                    }
                });
            }
        });

        return { eventTypeIds, organizationIds };
    }

    /**
     * Accumulate special field IDs from report data using selected field metadata
     */
    accumulateSpecialFieldIdsFromReport(
        reportData: { [key: string]: string | number }[],
        selectedFieldInfos: ReportingFieldInfo[],
    ): { eventTypeIds: Set<number>; organizationIds: Set<number> } {
        const eventTypeIds = new Set<number>();
        const organizationIds = new Set<number>();

        // Build a map of field codes to their field types for quick lookup
        const fieldTypeMap = new Map<string, string | undefined>();
        selectedFieldInfos.forEach((field) => {
            if (field.fieldType === "EVENT_TYPE" || field.fieldType === "ORGANIZATION") {
                fieldTypeMap.set(field.reportingFieldCode, field.fieldType);
            }
        });

        // Extract IDs from report data for special field types
        reportData.forEach((row) => {
            // Iterate over row keys (reporting field codes or special keys like form_name, cont_id)
            Object.keys(row).forEach((fieldCode) => {
                const fieldType = fieldTypeMap.get(fieldCode);
                if (!fieldType) return; // Not a special field type

                const value = row[fieldCode];
                if (value !== null && value !== undefined && !isNaN(Number(value))) {
                    const id = Number(value);
                    if (fieldType === "EVENT_TYPE") {
                        eventTypeIds.add(id);
                    } else if (fieldType === "ORGANIZATION") {
                        organizationIds.add(id);
                    }
                }
            });
        });

        return { eventTypeIds, organizationIds };
    }

    /**
     * Load names for special field IDs using a single call per type (set-based, DRY)
     */
    async loadNamesForSpecialFieldIds(
        eventTypeIds: Set<number>,
        organizationIds: Set<number>,
    ): Promise<Map<string, string>> {
        const nameMap = new Map<string, string>();

        if (eventTypeIds.size === 0 && organizationIds.size === 0) {
            return nameMap;
        }

        try {
            // Load event type names
            if (eventTypeIds.size > 0) {
                const eventTypeNames = await DataIngestionService.getObjectNames(
                    "EVENT_TYPE",
                    Array.from(eventTypeIds),
                );
                Object.entries(eventTypeNames).forEach(([key, name]) => {
                    nameMap.set(key, name);
                });
            }

            // Load organization names
            if (organizationIds.size > 0) {
                const organizationNames = await DataIngestionService.getObjectNames(
                    "ORGANIZATION",
                    Array.from(organizationIds),
                );
                Object.entries(organizationNames).forEach(([key, name]) => {
                    nameMap.set(key, name);
                });
            }
        } catch (error) {
            console.error("Error loading special field names:", error);
        }

        return nameMap;
    }

    /**
     * Load names for special field IDs from submissions (convenience wrapper)
     */
    async loadSpecialFieldNames(submissions: IngestionSubmission[]): Promise<Map<string, string>> {
        const { eventTypeIds, organizationIds } = this.accumulateSpecialFieldIds(submissions);
        return await this.loadNamesForSpecialFieldIds(eventTypeIds, organizationIds);
    }

    getFieldColumns(submissions: IngestionSubmission[]): string[] {
        const fieldCodes = new Set<string>();

        for (const submission of submissions) {
            if (submission.answers) {
                for (const answer of submission.answers) {
                    const fieldCode = answer.fieldDefinition?.reportingFieldCode;
                    if (fieldCode) {
                        fieldCodes.add(fieldCode);
                    }
                }
            }
        }

        return Array.from(fieldCodes).sort();
    }

    /**
     * Get field value for search submissions with special field name resolution
     */
    getFieldValue(submission: IngestionSubmission, fieldCode: string, specialFieldNames?: Map<string, string>): string {
        if (!submission.answers) {
            return "";
        }

        const answer = submission.answers.find(
            (a: IngestionFieldAnswer) => a.fieldDefinition?.reportingFieldCode === fieldCode,
        );

        if (!answer) {
            return "";
        }

        const fieldType = answer.fieldDefinition?.fieldType;
        const answerRaw = answer.answerRaw;

        // Resolve special field names if available
        const resolvedValue = this.resolveSpecialFieldValue(fieldType, answerRaw, specialFieldNames);
        if (resolvedValue !== null) {
            return resolvedValue;
        }

        // Check if this is a date/time field and format accordingly
        if (answer.fieldDefinition?.answerType === AnswerType.DATETIME) {
            return this.formatDateTime(answerRaw);
        }

        return this.formatValue(answerRaw);
    }

    /**
     * Get field value for report rows with special field name resolution (DRY)
     */
    getReportFieldValue(
        row: { [key: string]: string | number },
        columnId: string,
        selectedFieldInfos: ReportingFieldInfo[],
        specialFieldNames: Map<string, string>,
    ): string {
        const value = row[columnId];
        if (value === null || value === undefined) {
            return "";
        }

        // Find the field info for this column to determine its type
        const fieldInfo = selectedFieldInfos.find((f) => {
            // Handle special column IDs that map to field codes
            if (columnId === "form_name") return f.reportingFieldCode === "form";
            if (columnId === "cont_id") return f.reportingFieldCode === "cont_id";
            return f.reportingFieldCode === columnId;
        });

        // Resolve special field names if this is a special field type
        if (fieldInfo?.fieldType === "EVENT_TYPE" || fieldInfo?.fieldType === "ORGANIZATION") {
            const resolvedValue = this.resolveSpecialFieldValue(fieldInfo.fieldType, value, specialFieldNames);
            if (resolvedValue !== null) {
                return resolvedValue;
            }
        }

        // Handle date/time fields if this is a datetime field
        if (fieldInfo?.answerType === AnswerType.DATETIME) {
            return this.formatDateTime(value);
        }

        return this.formatValue(value);
    }

    /**
     * Resolve special field value (EVENT_TYPE, ORGANIZATION) to name if available (DRY helper)
     */
    resolveSpecialFieldValue(
        fieldType: string | undefined,
        value: string | number | null | undefined,
        specialFieldNames: Map<string, string> | undefined,
    ): string | null {
        if (!fieldType || !value || !specialFieldNames) {
            return null;
        }

        const numValue = Number(value);
        if (isNaN(numValue)) {
            return null;
        }

        // getObjectNames already appends the type to the key
        const key = `${numValue}-${fieldType}`;
        return specialFieldNames.get(key) || null;
    }

    formatDateTime(value: any): string {
        if (!value) {
            return "";
        }

        try {
            const format = this.dateTimeFormat();
            if (format) {
                return S25Datefilter.transform(value, format);
            }
            // Fallback to basic date formatting if no format loaded yet
            return this.formatDate(value);
        } catch (error) {
            console.error("Error formatting datetime:", error);
            // Fallback to basic date formatting
            return this.formatDate(value);
        }
    }

    formatValue(value: any): string {
        if (value === null || value === undefined) {
            return "";
        }

        if (typeof value === "object") {
            return JSON.stringify(value);
        }

        return String(value);
    }

    formatDate(date: any): string {
        if (!date) {
            return "";
        }

        try {
            const d = new Date(date);
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } catch (error) {
            return String(date);
        }
    }
}
